import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {CompactFooter} from 'components/CompactFooter'
import {MobileOverlays} from 'components/MobileOverlays/MobileOverlays'
import {HomeLinks} from 'components/Seo/HomeLinks'
import cookieConfig from 'config/cookies/web2AppBottomSheet'
import {TopBannerProvider} from 'contexts/TopBannerContext'
import {useIsPageSearchBoxOpen} from 'hooks/searchBox'
import {getIsSyntheticTest} from 'modules/meta/selectors'
import dynamic from 'next/dynamic'

import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {trackHomePageMRP} from '@vio/core/src/_web/utils/analytics/trackPerformanceMetrics'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {getCookie} from '@vio/core/src/_web/utils/cookies'

import {Footer} from '../Footer'
import {ComingSoonSection} from './ComingSoonSection'
import {Header} from './Header'
import {HeroSection} from './HeroSection'
import {MobileAppSection} from './MobileAppSection'
import {PowerFeaturesSection} from './PowerFeaturesSection'
import {SavingSection} from './SavingSection'
import {SocialProofSection} from './SocialProofSection'
import {SupportSection} from './SupportSection'
import {WidgetsSection} from './WidgetsSection'

const Web2AppBottomSheet = dynamic(
  () => import('./Web2AppBottomSheet').then(mod => mod.Web2AppBottomSheet),
  {ssr: false}
)
const CookieBanner = dynamic(() => import('components/CookieBanner'), {
  ssr: false
})

interface Props {
  hasWidgetContext?: boolean
}
export const Home: React.FC<Props> = ({hasWidgetContext = true}) => {
  const [initialPageRender, setInitialPageRender] = useState(true)
  const dispatch = useDispatch()
  const {brand} = useBrand()
  const {
    shell: {
      hasFullBrandFooter,
      hasHomeComingSoonSection,
      hasHomeMarketingSections,
      hasTrustpilot,
      showsHomeSrpLinks
    },
    retention: {hasGetTheApp}
  } = useBrandCapabilities()
  const isSearchBoxOpen = useIsPageSearchBoxOpen()
  const web2AppBottomSheetCookie = getCookie(cookieConfig.name)
  const isSyntheticTest = useSelector(getIsSyntheticTest)

  useEffect(() => {
    trackHomePageMRP(dispatch)
  }, [dispatch])

  const {ref: heroSectionRef, inView: isHeroSectionInView} = useInView({
    threshold: 0.1
  })

  const {ref: heroAndWidgetsRef, inView: isHeroAndWidgetInView} = useInView({
    threshold: 0.1,
    initialInView: true
  })

  useEffect(() => {
    setTimeout(() => setInitialPageRender(false), 400)
  }, [])

  return (
    <TopBannerProvider>
      <CookieBanner />

      <Header
        isStickyHeaderVisible={
          !initialPageRender && (!isHeroAndWidgetInView || isSearchBoxOpen)
        }
      />
      <div ref={heroAndWidgetsRef}>
        <div ref={heroSectionRef}>
          <HeroSection />
        </div>
        <WidgetsSection hasContext={hasWidgetContext} />
      </div>

      {hasHomeComingSoonSection && <ComingSoonSection />}

      {hasHomeMarketingSections && (
        <>
          <SavingSection />
          {hasTrustpilot && <SocialProofSection />}
          <PowerFeaturesSection />
          {hasGetTheApp && <MobileAppSection />}
          <SupportSection />
        </>
      )}
      {hasFullBrandFooter ? (
        <Footer brand={brand} linksSlot={showsHomeSrpLinks && <HomeLinks />} />
      ) : (
        <CompactFooter />
      )}

      {!web2AppBottomSheetCookie && !isSyntheticTest && (
        <Web2AppBottomSheet
          isVisible={!initialPageRender && !isHeroSectionInView}
        />
      )}
      <MobileOverlays />
    </TopBannerProvider>
  )
}
